import type { ContentBlock } from './content-blocks';
import type { ArrayValues, ValueOf } from 'type-fest';

export const ARTIFACT_TYPES = {
  NEWS_ITEM: 'news_item',
  NEWS_ROLLUP: 'news_rollup',
  PROSPECT_QUOTE: 'prospect_quote',
  PROSPECTS_QUOTES_ROLLUP: 'prospects_quotes_rollup',
  OBJECTION_HANDLING: 'objection_handling_quote',
  OBJECTION_HANDLING_ROLLUP: 'objection_handling_rollup',
  WIN_LOSS_STORY: 'winloss_story',
  // Naming oddity from backend: `talk_tracks` (with underscore) for the
  // leaf, `talktracks_rollup` (no underscore between talk and tracks)
  // for the rollup. Mirror backend slugs verbatim.
  WIN_LOSS_ROLLUP: 'winloss_rollup',
  // Win Loss campaign interview analysis. Like hiring_signal_block, a
  // DB-registered block-artifact with no dedicated payload type — content
  // is the block stream.
  WIN_LOSS_CAMPAIGN_INTERVIEW_ANALYSIS: 'campaign_interview_analysis',
  WIN_LOSS_CAMPAIGN_ROLLUP: 'campaign_rollup_analysis',
  TALK_TRACKS: 'talk_tracks',
  TALK_TRACKS_ROLLUP: 'talktracks_rollup',
  // Topic-rollup generation (ENG-2218): cluster recent leaf artifacts into
  // topics. These supersede the older `*_rollup` trending digests above and
  // are what the home feed serves. Backend slugs verbatim (note the singular
  // `prospect_quotes` and underscored `talk_tracks`).
  OBJECTION_HANDLING_ROLLUP_TOPICS: 'objection_handling_rollup_topics',
  PROSPECT_QUOTES_ROLLUP_TOPICS: 'prospect_quotes_rollup_topics',
  TALK_TRACKS_ROLLUP_TOPICS: 'talk_tracks_rollup_topics',
  WIN_LOSS_ROLLUP_TOPICS: 'winloss_rollup_topics',
  // The brief itself carries no payload type — its content is the
  // block stream, and the renderer dispatches by ContentBlock type.
  // The slug exists in this map so type guards (provenance mapping,
  // ArtifactType union) recognize it.
  DAILY_BRIEF: 'daily_brief',
  CUSTOMER_QUOTE: 'customer_quote',
  CUSTOMER_QUOTES_ROLLUP: 'customer_quotes_rollup',
  BLINDSPOTS: 'blindspots',
  // Competitor hiring/talent activity. Like daily_brief, carries no
  // dedicated payload type — content is the block stream. DB-registered on
  // the backend (surfaced via /block-artifacts/summary/, not stak's static
  // config); slug verified against staging. Note the `_block` suffix, unlike
  // the older leaf slugs. A thin `hiring_signal_rollup` also exists on the
  // backend but is intentionally excluded (1 artifact, looks experimental).
  HIRING_SIGNAL: 'hiring_signal_block',
  // Customer-praise quotes approved for sharing. Leaf type (list mode),
  // successor to customer_quote in the Customer Proof tab.
  PROOF_POINTS: 'proof_points',
} as const;

export type ArtifactType = ValueOf<typeof ARTIFACT_TYPES>;

export type ArtifactCompetitor = {
  company_org_unit_id: string | null;
  company_name: string;
  product_org_unit_id: string | null;
  product_name: string | null;
};

export type ArtifactBase = {
  id: string;
  artifact_type: ArtifactType;
  schema_version: number;
  stak_name: string;
  stak_version: string | null;
  created_at: string;
  origin_id: string;
  origin_index: number;
  version: number;
  competitors: ArtifactCompetitor[];
  summary: string | null;
};

export type NewsItemSource = {
  url: string;
  source_name: string;
  source_date: string;
};

export type NewsItemPayload = {
  summary: string;
  urgency: UrgencyLevel;
  source: NewsItemSource;
  competitor: {
    org_unit_id: string | null;
    product_org_unit_id: string | null;
  };
  body_markdown: string;
  why_it_matters: string;
};

export const PROSPECT_QUOTE_CATEGORIES = [
  'product',
  'pricing_and_contracts',
  'integrations',
  'ai_and_automation',
  'customer_experience',
  'reporting_and_analytics',
  'scalability_and_performance',
  'security_and_compliance',
  'company_perception',
  'supply_chain_and_distribution',
  'sales_and_demo_experience',
  'ui_ux',
  'other',
] as const;

export type ProspectQuoteCategory = ArrayValues<
  typeof PROSPECT_QUOTE_CATEGORIES
>;

// Schema declares only positive | negative, but the API returns neutral in practice
export type ProspectQuoteSentiment = 'positive' | 'negative' | 'neutral';

export type ProspectQuotePayload = {
  quote: string;
  quote_normalized: string;
  title: string;
  summary: string;
  sentiment: ProspectQuoteSentiment;
  category: ProspectQuoteCategory;
  topic: string;
  line_number: string;
  created_at: string;
  call_date: string;
  speaker_name: string;
  speaker_company: string;
  speaker_role?: string;
  start_time?: string;
  snippet_url?: string;
};

export type ObjectionSource = {
  call_id: string;
  call_date: string;
  seller_name: string | null;
  buyer_name: string | null;
  buyer_role: string | null;
  account_name: string | null;
  opportunity_id: string | null;
};

export const OBJECTION_CATEGORIES = [
  'budget_pricing',
  'features_functionality',
  'implementation',
  'value_roi',
  'timing_urgency',
  'internal_alignment',
  'risk_security',
  'other',
] as const;

export type ObjectionCategory = ArrayValues<typeof OBJECTION_CATEGORIES>;

export type QualityScore = 1 | 2 | 3 | 4 | 5;

export type ObjectionHandlingPayload = {
  source: ObjectionSource;
  summary: string;
  category: ObjectionCategory;
  competitor: {
    org_unit_id: string | null;
    product_org_unit_id: string | null;
  };
  created_at: string;
  artifact_id: string;
  snippet_url: string;
  handled_well: boolean;
  artifact_type: 'objection';
  quality_score: QualityScore;
  objection_text: string;
  objection_normalized: string;
  seller_response_text: string;
  seller_response_normalized: string;
  prospect_reaction_text: string;
  prospect_reaction_normalized: string;
};

export const WIN_LOSS_STORY_TYPES = ['win_story', 'loss_story'] as const;

export type WinLossStoryType = ArrayValues<typeof WIN_LOSS_STORY_TYPES>;

export const WIN_LOSS_DEAL_THREAT_LEVELS = [
  'seriously_evaluating',
  'currently_using_considering_replacing',
] as const;

export type WinLossDealThreatLevel = ArrayValues<
  typeof WIN_LOSS_DEAL_THREAT_LEVELS
>;

export type WinLossStoryCompetitor = {
  company_name: string;
  product_name?: string | null;
  company_org_unit_id?: string | null;
  product_org_unit_id?: string | null;
};

export type WinLossDealInformation = {
  won: boolean;
  closed: boolean;
  remote_id: string;
  company_id: string;
  created_at: string;
  updated_at: string;
  stage?: string | null;
  amount?: number | null;
  currency?: string | null;
  account_id?: string | null;
  close_date?: string | null;
  remote_url?: string | null;
  loss_reason?: string | null;
  account_name?: string | null;
  opportunity_type?: string | null;
  stage_prior_to_closing?: string | null;
};

export type WinLossStoryPayload = {
  summary: string;
  story_type: WinLossStoryType;
  story_title: string;
  story_markdown: string;
  key_differentiator: string;
  deal_threat_level: WinLossDealThreatLevel;
  deal_information: WinLossDealInformation;
  competitors: WinLossStoryCompetitor[];
  source_urls: string[];
};

export type RollupSourceDetails = {
  source: 'rollup';
  start_date: string;
  end_date: string;
  item_artifact_type: string;
};

// Shared payload shape for all rollup artifacts. Individual artifact types
// will adopt a similar content-blocks structure later; for now this only
// applies to rollups.
//
// The backend is currently inconsistent across rollup types:
// - news_rollup uses `content_blocks`
// - objection_handling_rollup + prospects_quotes_rollup use `content`
// We accept both; components read via a helper.
// Similarly, news_rollup omits `source_details`; others include it with the
// rollup period. We accept the optionality.
// Legacy rollup blocks straddle the old/new contract:
// - Rollup APIs still emit `type: 'markdown'` (with either a `text` or
//   `markdown` field), while the new ContentBlock vocabulary uses
//   `type: 'prose'` exclusively.
// - News_rollup v4 emits `type: 'artifact_ref'` (short alias) for what
//   the new vocabulary calls `'artifact_reference'`.
// The legacy adapter migrates both at the read boundary.
export type LegacyProseBlock = {
  type: 'markdown';
  text?: string;
  markdown?: string;
};

export type LegacyArtifactRefBlock = {
  type: 'artifact_ref';
  artifact_id: string;
};

export type LegacyRollupBlock =
  ContentBlock | LegacyProseBlock | LegacyArtifactRefBlock;

export type RollupPayload = {
  summary: string;
  subtitle: string;
  content?: LegacyRollupBlock[];
  content_blocks?: LegacyRollupBlock[];
  source_details?: RollupSourceDetails;
  // Schema marks `metadata` as required + non-nullable, but real payloads
  // sometimes ship without it (observed on news_rollup). Type permissively
  // to match reality; see "Known schema vs. reality mismatches" in CLAUDE.md.
  metadata?: {
    boolean?: Record<string, boolean>;
    number?: Record<string, number>;
    enum?: Record<string, string>;
  };
  // The LLM emits per-rollup bookkeeping fields that vary by type (e.g.
  // `quotes`, `period`, `themes`). Frontend ignores these for now; the
  // index signature keeps us from having to model each.
  [key: string]: unknown;
};

// Backend currently ships this field inside payload.metadata.number for news
// rollups. Helper type lifts it out of the untyped number bucket.
export function getRollupHighestUrgency(
  payload: RollupPayload,
): UrgencyLevel | undefined {
  const value = payload.metadata?.number?.highest_urgency;
  if (value === 1 || value === 2 || value === 3 || value === 4 || value === 5) {
    return value;
  }
  return undefined;
}

// Returns the rollup content blocks (still in legacy shape) — callers
// must migrate via the adapter before passing to the renderer.
export function getRollupContentBlocks(
  payload: RollupPayload,
): LegacyRollupBlock[] {
  return payload.content_blocks ?? payload.content ?? [];
}

export type NewsItemArtifact = ArtifactBase & {
  artifact_type: 'news_item';
  payload: NewsItemPayload;
};

export type NewsRollupArtifact = ArtifactBase & {
  artifact_type: 'news_rollup';
  payload: RollupPayload;
};

export type ProspectQuoteArtifact = ArtifactBase & {
  artifact_type: 'prospect_quote';
  payload: ProspectQuotePayload;
};

export type ProspectsQuotesRollupArtifact = ArtifactBase & {
  artifact_type: 'prospects_quotes_rollup';
  payload: RollupPayload;
};

export type ObjectionHandlingArtifact = ArtifactBase & {
  artifact_type: 'objection_handling_quote';
  payload: ObjectionHandlingPayload;
};

export type ObjectionHandlingRollupArtifact = ArtifactBase & {
  artifact_type: 'objection_handling_rollup';
  payload: RollupPayload;
};

export type WinLossStoryArtifact = ArtifactBase & {
  artifact_type: 'winloss_story';
  payload: WinLossStoryPayload;
};

export type Artifact =
  | NewsItemArtifact
  | NewsRollupArtifact
  | ProspectQuoteArtifact
  | ProspectsQuotesRollupArtifact
  | ObjectionHandlingArtifact
  | ObjectionHandlingRollupArtifact
  | WinLossStoryArtifact;

export function isNewsItemArtifact(
  artifact: Artifact,
): artifact is NewsItemArtifact {
  return artifact.artifact_type === 'news_item';
}

export function isNewsRollupArtifact(
  artifact: Artifact,
): artifact is NewsRollupArtifact {
  return artifact.artifact_type === 'news_rollup';
}

export function isProspectQuoteArtifact(
  artifact: Artifact,
): artifact is ProspectQuoteArtifact {
  return artifact.artifact_type === 'prospect_quote';
}

export function isProspectsQuotesRollupArtifact(
  artifact: Artifact,
): artifact is ProspectsQuotesRollupArtifact {
  return artifact.artifact_type === 'prospects_quotes_rollup';
}

export function isObjectionHandlingArtifact(
  artifact: Artifact,
): artifact is ObjectionHandlingArtifact {
  return artifact.artifact_type === 'objection_handling_quote';
}

export function isObjectionHandlingRollupArtifact(
  artifact: Artifact,
): artifact is ObjectionHandlingRollupArtifact {
  return artifact.artifact_type === 'objection_handling_rollup';
}

export function isWinLossStoryArtifact(
  artifact: Artifact,
): artifact is WinLossStoryArtifact {
  return artifact.artifact_type === 'winloss_story';
}

export type ArtifactView = 'preview' | 'full';

// Open-vocabulary slug for the API. The backend type registry is the
// source of truth (ENG-1131) and can ship a type the frontend hasn't
// seen yet — anything that just forwards the slug to the API should
// type it as `ArtifactTypeSlug`, not the closed `ArtifactType` union.
export type ArtifactTypeSlug = string;

export type ArtifactListParams = {
  // Omit to list across all artifact types (block-artifacts endpoint only;
  // the legacy /v0/artifacts/ endpoint still requires it).
  artifact_type?: ArtifactTypeSlug;
  competitor_org_unit_id?: string;
  from_date?: string;
  to_date?: string;
  limit?: number;
  offset?: number;
  view?: ArtifactView;
  // JSON-encoded array of LeafFilter (stak#1688). Caller is responsible
  // for encoding via `encodeFilters` and pruning incomplete entries.
  filters?: string;
  // Lexical search across title/description, block text, and attribute
  // values (stak FTS). Block-artifacts endpoint only.
  q?: string;
};

export type ArtifactByIdParams = {
  id: string;
  view?: ArtifactView;
  original?: boolean;
};

export type ArtifactListResponse<T extends ArtifactBase = Artifact> = {
  items: T[];
  total_count: number;
  // True when a search matched more rows than the backend's count cap;
  // total_count then holds the cap and should render as "1,000+". Absent
  // on the legacy /v0/artifacts endpoint.
  total_count_capped?: boolean;
  page: number;
  total_pages: number;
};

export const URGENCY_LABELS = {
  1: 'Low',
  2: 'Low',
  3: 'Medium',
  4: 'High',
  5: 'Critical',
} as const;

export const URGENCY_COLORS = {
  1: 'gray',
  2: 'gray',
  3: 'yellow',
  4: 'red',
  5: 'red',
} as const;

export type UrgencyLevel = keyof typeof URGENCY_LABELS;
